// OBS Studio scene-collection JSON builder.
// Mirrors the scene-collection format produced by OBS 30+ (version: 2).
// Each granted overlay becomes a scene whose layout source is a browser
// source pointing at that overlay's URL. Game Capture is a shared source on
// every scene EXCEPT the full-screen ones (see NO_GAME_CAPTURE_SLUGS) and the
// KEC packages' camera commentator scene (see COMMENTATOR_SOURCES and
// commentatorWindows), pointed at the package's game window (see
// GAME_WINDOW) — the user configures device/window once, if at all.
// Sound, by package (see SOUND). As built for Tekken 8: OBS's two global
// audio devices, which play on every scene, Desktop Audio, the PC's own
// sound (the game through the capture app, anything else it plays), and
// Mic/Aux, the microphone. Nothing else records the PC: an Audio Output
// Capture in every scene and the Game Capture's own audio both heard the
// same speakers again, so the stream carried the PC's sound twice, or three
// times on gameplay scenes (removed 2026-10-17). For VALORANT (the club's
// ask, 2026-10-26): no Desktop Audio; the game's sound comes with the Game
// Capture ("Capture audio"), so it plays only on In-Match, the one scene
// that shows the game (the commentator scene had a capture of its own, at
// 70%, until 2026-10-29: see BOOTH_COMMENTATOR_PACKAGES); and a music source
// in every scene but In-Match, one file looping on at 35% (see musicSource and
// MUSIC_VOLUME), which the stinger's audio crossfade fades out on the cut to
// the match and back in after it, and which keeps playing off air so it
// comes back mid-track.
// VALORANT's mic is the HyperX SoloCast 2 (the user's, 2026-10-26; see MIC):
// the global Mic/Aux is named Commentator Mic and downmixed to mono (one
// voice on one capsule; a USB mic that Windows exposes as stereo would
// otherwise come out of one side). Which Windows device it is can't be in
// the export: Windows names each input by a GUID of the PC's own, so the
// console's OBS Control picks it by name on connect (obsStage.js pickMic,
// obsControl.js pickMicDevice); elsewhere, OBS > Settings > Audio >
// Mic/Auxiliary Audio.

const PREV_VER = 536936450;
const SCENE_CANVAS_UUID = '6c69626f-6273-4c00-9d88-c5136d61696e';

// Full-screen interstitial and reveal scenes cover the whole canvas, so a Game
// Capture feed behind them would be hidden — omit it from these. Every
// other scene (In-Match, Camera Feed, and any custom overlay that composites
// over live gameplay) keeps the shared Game Capture source. Denylist rather
// than allowlist so a renamed/custom gameplay overlay (non-canonical slug like
// 'in-match-sponsor') still gets its game feed.
const NO_GAME_CAPTURE_SLUGS = new Set([
  'starting-soon',
  'be-right-back',
  'top-8',
  'thank-you',
  'up-next',
  // VALORANT's Bracket screen (the whole bracket, between matches).
  'bracket',
]);

// Overlays whose browser source ships with a Chroma Key filter pre-applied.
// The camera-feed overlay fills its two cam boxes with green (#00FF00) as a
// chroma target; keying it out lets the sources composited behind show
// through. Baking the filter into the export means the TO doesn't add it by
// hand after import.
const CHROMA_KEY_SLUGS = new Set(['camera-feed']);

// Overlays whose browser source reloads every time its scene becomes active
// ("Refresh browser when scene becomes active" in OBS). The Top 8 layouts are
// cut-to scenes shown occasionally, so a scene-activation refresh reloads a
// fresh, fully-updated Top 8 without the TO touching the source by hand.
// Winner Screen is the same shape — a cut-to scene the auto-director (or a
// hotkey) switches to right after a stamp lands, and its 5s /state poll
// could otherwise air the PREVIOUS winner for a beat; the activation reload
// fetches current state and plays the reveal from the start.
const RESTART_WHEN_ACTIVE_SLUGS = new Set(['top-8', 'top-8-bracket', 'winner-screen']);

// Overlays whose browser source shuts down while its scene is hidden
// ("Shutdown source when not visible" in OBS). Paired with the refresh above
// on the same scenes: frees the source off-screen and reloads it fresh when
// the scene comes back.
const SHUTDOWN_WHEN_HIDDEN_SLUGS = new Set(['top-8', 'top-8-bracket', 'winner-screen']);

// The camera-feed overlay shows two on-screen cam boxes. Each gets its own
// capture source, sized to fit its box on the 1920×1080 OBS canvas the browser
// source renders at (measured from the overlay): Player Cam (window capture) on
// the left, Game Capture on the right. 933×525 each (16:9). The two feeds are
// butted together at centre (x=960) with 27px margins on the outer edges.
const CAMERA_FEED_SLUG = 'camera-feed';
export const IN_MATCH_SLUG = 'in-match';

// The swapped in-match twin: the SAME page with ?flip=1, which the overlay
// XORs with the console's swap toggle. One grant, one token — the param is
// purely client-side. Shared by the vMix export and the console Links tab.
export const IN_MATCH_SWAPPED_SUFFIX = ' (Swapped)';
export const swappedOverlayUrl = (url) => `${url}${url.includes('?') ? '&' : '?'}flip=1`;
const CAM_BOXES = {
  player: { x: 27, y: 149, w: 933, h: 525 },
  game:   { x: 960, y: 149, w: 933, h: 525 },
};

// Packages whose events have a stinger: the scene collection gets it as a
// Stinger transition, the current one, cutting to the next scene at `cutMs`,
// when the stinger covers the whole screen (the KEC ones are the client's
// pick, Envato's Cartoon Fire Transitions no. 02 with the club's logo, built
// outside the repo by E:\Envato\_tools\fire-stinger.mjs: VALORANT's in the
// club's pink, Marvel Rivals' in the game's yellow and blue with the navy
// logo, both on the same timing). OBS plays a stinger from a file on the PC,
// never from a web address, so the transition points at `path`, and the
// console offers the hosted file (`file`, saved as `saveAs`) beside the
// export button: kept at `path`, the stinger just works; anywhere else, OBS's
// Missing Files dialog asks for it when the collection loads. Each game has a
// path of its own, so both can sit on one PC; VALORANT's is the one OBS
// setups already point at. Preload is on, as on the setup the club tested.
export const STINGERS = {
  valorant: {
    name: 'KEC Stinger',
    file: '/overlay/images/kec/stinger.webm',
    saveAs: 'KEC-stinger.webm',
    path: 'C:/StreamGG/KEC-stinger.webm',
    cutMs: 907,
  },
  'marvel-rivals': {
    name: 'KEC Marvel Rivals Stinger',
    file: '/overlay/images/kec/stinger-marvel-rivals.webm',
    saveAs: 'KEC-Marvel-Rivals-stinger.webm',
    path: 'C:/StreamGG/KEC-Marvel-Rivals-stinger.webm',
    cutMs: 907,
  },
};

// Settings OBS leaves at their defaults aren't written, as OBS does: the
// transition point is in milliseconds by default. audio_fade_style 1 is
// "Crossfade": the old scene's sound fades out as the new one's fades in, so
// a source in both scenes (the music) plays on through a cut and one in only
// one of them (the music, on the cut to In-Match) fades. The default, fade
// out to the transition point then fade in, dips every sound at every cut.
const stingerTransition = (s, { crossfade = false } = {}) => ({
  name: s.name,
  id: 'obs_stinger_transition',
  settings: { path: s.path, transition_point: s.cutMs, preload: true, ...(crossfade ? { audio_fade_style: 1 } : {}) },
});

const newId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 14)}`;

// OBS_SOURCE_FLAG_FORCE_MONO (libobs obs-source.h): the Mono box in OBS's
// Advanced Audio Properties, kept in the source's flags.
const FORCE_MONO = 1 << 1;

const audioBusDevice = (name, kind, { mono = false } = {}) => ({
  prev_ver: PREV_VER,
  name,
  uuid: newId(),
  id: kind,
  versioned_id: kind,
  settings: { device_id: 'default' },
  mixers: 255,
  sync: 0,
  flags: mono ? FORCE_MONO : 0,
  volume: 1.0,
  balance: 0.5,
  enabled: true,
  muted: false,
  'push-to-mute': false,
  'push-to-mute-delay': 0,
  'push-to-talk': false,
  'push-to-talk-delay': 0,
  hotkeys: {
    'libobs.mute': [],
    'libobs.unmute': [],
    'libobs.push-to-mute': [],
    'libobs.push-to-talk': [],
  },
  deinterlace_mode: 0,
  deinterlace_field_order: 0,
  monitoring_type: 0,
  private_settings: {},
});

// Each package's sound (see the top of the file): whether OBS's global
// Desktop Audio is in the collection, whether the Game Captures carry the
// game's sound, and a music file in every scene but In-Match. The music is a
// path on the PC, like the stinger's: OBS's Missing Files dialog asks for the
// file when the collection loads elsewhere. Which track it is, the admin
// picks in Package Art (functions/_lib/layouts.js, MUSIC); the console's
// Music button downloads that track under the file's name (saveAs).
export const MUSIC = {
  valorant: { name: 'Background Music', path: 'C:/StreamGG/KEC-music.mp3', saveAs: 'KEC-music.mp3' },
};
const SOUND = {
  valorant: {
    desktopAudio: false,
    captureAudio: true,
    music: MUSIC.valorant,
  },
};
const DEFAULT_SOUND = { desktopAudio: true, captureAudio: false, music: null };
const soundOf = (packageSlug) => SOUND[packageSlug] ?? DEFAULT_SOUND;

// A package's microphone, where it is a known one (see the top of the file):
// the global Mic/Aux's name in the collection, whether it is downmixed to
// mono, and the device the OBS Control picks for it on connect: match,
// against the names Windows gives its inputs ("Microphone (HyperX SoloCast
// 2)"), prefer among several matches, model for the log and the mixer. A
// package not listed keeps OBS's Mic/Aux on the default device, stereo.
export const MIC = {
  valorant: { name: 'Commentator Mic', model: 'HyperX SoloCast 2', match: /solocast/i, prefer: /solocast\s*2/i, mono: true },
};
export const micOf = (packageSlug) => MIC[packageSlug] ?? null;

// Where the music starts: OBS's linear volume, 35% in Advanced Audio
// Properties, about -9 dB on the mixer's fader (the user's ask, 2026-10-28:
// under the commentators and the game). A fader moved in OBS stays moved, in
// OBS's own copy of the collection; this is only where an import starts.
export const MUSIC_VOLUME = 0.35;

// A Media Source playing one file on a loop: the same source in every scene
// but In-Match, so it plays on through a cut, and kept running off those
// scenes (restart_on_activate off) so it comes back mid-track, not from the
// top.
const musicSource = (name, path, volume = MUSIC_VOLUME) => ({
  prev_ver: PREV_VER,
  name,
  uuid: newId(),
  id: 'ffmpeg_source',
  versioned_id: 'ffmpeg_source',
  settings: {
    local_file: path,
    looping: true,
    restart_on_activate: false,
  },
  mixers: 255,
  sync: 0,
  flags: 0,
  volume,
  balance: 0.5,
  enabled: true,
  muted: false,
  'push-to-mute': false,
  'push-to-mute-delay': 0,
  'push-to-talk': false,
  'push-to-talk-delay': 0,
  hotkeys: {
    'libobs.mute': [],
    'libobs.unmute': [],
    'libobs.push-to-mute': [],
    'libobs.push-to-talk': [],
    'MediaSource.Restart': [],
    'MediaSource.Play': [],
    'MediaSource.Pause': [],
    'MediaSource.Stop': [],
  },
  deinterlace_mode: 0,
  deinterlace_field_order: 0,
  monitoring_type: 0,
  private_settings: {},
});

// What a package's Game Captures are pointed at ("Capture specific window",
// capture_mode 'window'): the window as OBS writes it, title:class:exe, and
// OBS's match priority (its enum: class 0, title 1, exe 2). Unlisted, the 4K
// Capture Utility window, the reference client's capture-card app, as their
// scene had it, priority 1 = "Window title must match"; operators on other
// capture software re-pick the window on import. VALORANT is captured on
// the stream PC itself (the observer client), so its captures are pointed
// at the game's own window, matched by executable (priority 2: the title,
// "VALORANT  " with two trailing spaces, and the class need not match), and
// nothing is re-picked. Alt-tabbing out of the game blacks any Game Capture
// while the game is in exclusive Fullscreen (Windows minimizes it and it
// stops drawing); the cure is the game's own Video settings, Windowed
// Fullscreen with Limit FPS in Background off, which no collection can carry
// (2026-10-28).
const CAPTURE_CARD = {
  window: '4K Capture Utility:HwndWrapper[4KCaptureUtility.exe;;566b2750-b110-44cf-bff0-a9376833f9d9]:4KCaptureUtility.exe',
  priority: 1,
};
export const GAME_WINDOW = {
  valorant: { window: 'VALORANT  :VALORANTUnrealWindow:VALORANT-Win64-Shipping.exe', priority: 2 },
};
const gameWindowOf = (packageSlug) => GAME_WINDOW[packageSlug] ?? CAPTURE_CARD;

// A Game Capture's audio is off where the global Desktop Audio hears the
// capture app, on where the package's sound comes from the captures (see
// SOUND).
const gameCaptureSource = ({ name = 'Game Capture', captureAudio = false, volume = 1.0, target = CAPTURE_CARD } = {}) => ({
  prev_ver: PREV_VER,
  name,
  uuid: newId(),
  id: 'game_capture',
  versioned_id: 'game_capture',
  settings: {
    window: target.window,
    capture_audio: captureAudio,
    capture_mode: 'window',
    priority: target.priority,
  },
  mixers: 255,
  sync: 0,
  flags: 0,
  volume,
  balance: 0.5,
  enabled: true,
  muted: false,
  'push-to-mute': false,
  'push-to-mute-delay': 0,
  'push-to-talk': false,
  'push-to-talk-delay': 0,
  hotkeys: {
    'libobs.mute': [],
    'libobs.unmute': [],
    'libobs.push-to-mute': [],
    'libobs.push-to-talk': [],
    hotkey_start: [],
    hotkey_stop: [],
  },
  deinterlace_mode: 0,
  deinterlace_field_order: 0,
  monitoring_type: 0,
  private_settings: {},
});

// OBS built-in "Green" Chroma Key preset. OBS fills defaults for anything
// omitted; the explicit values match its stock green key so the result is
// predictable across versions. id v2 matches the filter OBS 28+ adds today.
const chromaKeyFilter = () => ({
  prev_ver: PREV_VER,
  name: 'Chroma Key',
  uuid: newId(),
  id: 'chroma_key_filter_v2',
  versioned_id: 'chroma_key_filter_v2',
  settings: {
    key_color_type: 'green',
    key_color: 65280,
    similarity: 400,
    smoothness: 80,
    spill: 100,
  },
  enabled: true,
  hotkeys: {},
});

// The reference client's Camera Feed / Commentator scenes use real USB
// cameras; we bake in their exact device IDs so their rig is plug-and-play.
// These are MACHINE-SPECIFIC paths — on any other PC OBS shows the source
// blank until the operator re-picks the camera. Logitech = 'Crowd' +
// 'Commentator Cam'; the face-cam "Video Capture Device" = the REDRAGON.
const COMMENTATOR_SLUG = 'commentator';
const CROWD_DEVICE = 'Logitech Webcam C925e:\\\\?\\usb#22vid_046d&pid_085b&mi_00#227&49e473b&0&0000#22{65e8773d-8f56-11d0-a3b9-00a0c9223196}\\global';
const CAM_DEVICE = 'REDRAGON Live Camera:\\\\?\\usb#22vid_09da&pid_2690&mi_00#226&1188e634&0&0000#22{65e8773d-8f56-11d0-a3b9-00a0c9223196}\\global';

// The KEC packages' commentator page (VALORANT, Marvel Rivals) has the
// commentators on camera: each window is a hole through the page, and OBS's
// cameras show through it from under the page. The console's "One camera for
// both commentators" switch draws two windows side by side or one larger
// one, so the scene has a camera fitted to each window of the pair, shown,
// and one fitted to the single window, hidden: the operator shows the ones
// that match the switch and picks each camera's device after import. There
// is no Game Capture behind it: it would show through the windows. The
// windows were measured on both pages at 1920x1080 with the design's Cameras
// settings (Kec's saved ones, 2026-10-16); moving them in Package Art means
// moving the cameras by hand. Each camera covers its window, cropped to it
// (a 4:3 webcam shows no bars), and stops while hidden ("Deactivate when not
// showing"), so the one-window camera can be the same device as one of the
// pair. Their own sound (a webcam's built-in mic) ships muted: the mic is
// OBS's global Mic/Aux (see the top of the file), and a second one would
// play over it.
export const CAMERA_COMMENTATOR_PACKAGES = new Set(['valorant', 'marvel-rivals']);
// Of those, the packages whose commentator page is a booth of two windows
// over other screens (VALORANT's page): in the pair layout the windows hold
// the Up Next overlay and the Bracket (the club's ask, 2026-10-29), so the
// scene gets those two browser sources, the same ones as their own scenes'
// (one page each), fitted into the windows (see COMMENTATOR_WINDOW_OVERLAYS
// and commentatorWindows), and one camera, hidden, under the one larger
// window of the console's "Camera only"; the OBS Control shows, hides and
// slides the three as the page's layout changes. Until 2026-10-29 the pair
// was the camera beside a Game Capture of the scene's own, at 70%, which OBS
// can't do across a cut: a Game Capture that leaves the screen signals the
// game's hook to stop (plugins/win-capture/game-capture.c, stop_capture),
// the event is one per game process, so In-Match's capture of the same game
// stopped with it and re-hooked after its 2 s retry, black and silent
// meanwhile, or kept a frozen frame. The game is captured on In-Match
// alone. Marvel Rivals' page keeps a camera per window (COMMENTATOR_CAMERAS
// below).
export const BOOTH_COMMENTATOR_PACKAGES = new Set(['valorant']);
export const COMMENTATOR_SOURCES = {
  camera: 'Commentator Camera',
};
// The overlays under the booth's pair of windows, by slug, and their
// sources' names in a collection: the overlays' own browser sources
// (layoutSourceName), drawn in their scenes and in the booth alike.
export const COMMENTATOR_WINDOW_OVERLAYS = { left: 'up-next', right: 'bracket' };
export const commentatorWindowSources = (overlays = []) => {
  const nameOf = (slug) => {
    const o = overlays.find((x) => x.slug === slug);
    return o ? layoutSourceName(o.name) : null;
  };
  return { left: nameOf(COMMENTATOR_WINDOW_OVERLAYS.left), right: nameOf(COMMENTATOR_WINDOW_OVERLAYS.right) };
};
// The cameras' source names, which the console's OBS control (obsStage.js)
// shows and hides to follow the page's layout.
export const COMMENTATOR_CAMERAS = {
  left: 'Left Commentator Camera',
  right: 'Right Commentator Camera',
  one: 'One Camera for Both',
};
const COMMENTATOR_WINDOWS = {
  left: { x: 321, y: 163, w: 624, h: 351 },
  right: { x: 975, y: 163, w: 624, h: 351 },
  one: { x: 528, y: 96, w: 864, h: 486 },
};

// Where the gameplay commentator page draws its windows, on the 1920×1080
// OBS canvas, from the page's own CSS ("booth" in valorant/commentator.html,
// on its 2560×1440 canvas): the pair's windows 832px wide, centred 436px
// either side of the middle, or the one window 1152px wide in the middle,
// each 16:9 and centred on the hero row (y 100–803); the admin's Cameras,
// Camera window and Gameplay window settings (Package Art) size and move
// them as the page does. `vars` are those settings as the public layout
// endpoint serves them (the CSS custom properties, e.g. '--cams-scale':
// '0.9'); none gives the design, the boxes above. The gameplay window's
// place off the screen (out) is where it slides to when the camera is
// alone, 60px past the right edge as on the page.
export function commentatorWindows(vars = {}) {
  const num = (name, fallback) => {
    const v = parseFloat(vars?.[name]);
    return Number.isFinite(v) ? v : fallback;
  };
  const K = 1920 / 2560;
  const cs = num('--cams-scale', 1);
  const cy = num('--cams-y', 0);
  const mid = 1280;
  const row = 451.5;
  const box = (cx, cyy, w) => {
    const h = w * 9 / 16;
    return { x: Math.round((cx - w / 2) * K), y: Math.round((cyy - h / 2) * K), w: Math.round(w * K), h: Math.round(h * K) };
  };
  const pairOf = (i) => box(
    mid + (i === 1 ? -436 : 436) * cs + num(`--cam${i}-x`, 0),
    row + cy + num(`--cam${i}-y`, 0),
    832 * cs * num(`--cam${i}-scale`, 1),
  );
  const game = pairOf(2);
  return {
    camera: { pair: pairOf(1), one: box(mid, row + cy, 1152 * cs) },
    game: { pair: game, out: { ...game, x: 1920 + Math.round(60 * K) } },
  };
}

const dshowSource = (name, deviceId, { deactivateWhenHidden = false, muted = false } = {}) => ({
  prev_ver: PREV_VER,
  name,
  uuid: newId(),
  id: 'dshow_input',
  versioned_id: 'dshow_input',
  settings: {
    ...(deviceId ? { video_device_id: deviceId, last_video_device_id: deviceId } : {}),
    active: true,
    ...(deactivateWhenHidden ? { deactivate_when_not_showing: true } : {}),
  },
  mixers: 255,
  sync: 0,
  flags: 0,
  volume: 1.0,
  balance: 0.5,
  enabled: true,
  muted,
  'push-to-mute': false,
  'push-to-mute-delay': 0,
  'push-to-talk': false,
  'push-to-talk-delay': 0,
  hotkeys: {
    'libobs.mute': [],
    'libobs.unmute': [],
    'libobs.push-to-mute': [],
    'libobs.push-to-talk': [],
  },
  deinterlace_mode: 0,
  deinterlace_field_order: 0,
  monitoring_type: 0,
  private_settings: {},
});

// TWT event-logo source, shipped HIDDEN in the In-Match scene — mirrors the
// reference client setup's "TWT LOGO" (an image over the in-match view), but
// pointed at our HOSTED copy so no local file is needed. It's off by default;
// the operator toggles it visible only in an emergency (the overlay normally
// carries the branding). OBS image sources load LOCAL files only, so the
// hosted asset goes in a browser source instead. The URL's origin is taken
// from the grant's own overlay URL (no hard-coded domain). Sized to the logo's
// ~1.47:1 aspect; the scene item scales/positions it over the in-match view.
const TWT_LOGO_NAME = 'TWT LOGO';
// A transparent wrapper page (not the raw .png) — a browser source pointed at
// a bare image renders it on the browser's opaque image-viewer background,
// which would show as a grey box in OBS. public/overlay/twt_logo.html centres
// the hosted logo on a transparent background instead.
const TWT_LOGO_PATH = '/overlay/twt_logo.html';
const originOf = (url) => { try { return new URL(url).origin; } catch { return ''; } };
const twtLogoSource = (url) => ({
  prev_ver: PREV_VER,
  name: TWT_LOGO_NAME,
  uuid: newId(),
  id: 'browser_source',
  versioned_id: 'browser_source',
  settings: { url, width: 600, height: 409 },
  mixers: 255,
  sync: 0,
  flags: 0,
  volume: 1.0,
  balance: 0.5,
  enabled: true,
  muted: false,
  'push-to-mute': false,
  'push-to-mute-delay': 0,
  'push-to-talk': false,
  'push-to-talk-delay': 0,
  hotkeys: {
    'libobs.mute': [],
    'libobs.unmute': [],
    'libobs.push-to-mute': [],
    'libobs.push-to-talk': [],
    'ObsBrowser.Refresh': [],
  },
  deinterlace_mode: 0,
  deinterlace_field_order: 0,
  monitoring_type: 0,
  private_settings: {},
});

// Standalone hosted legal-line source, added to every scene but shipped HIDDEN
// — the TO flips it on per scene once they've confirmed it sits clear of that
// scene's own chrome. Hosting the "TEKKEN(TM)8 & (c) Bandai Namco Entertainment
// Inc." strip as one page instead of baking it into each overlay's HTML means
// updating /overlay/bandai_copyright.html makes every scene follow on refresh,
// with no per-grant "Use default" re-apply. Full-frame transparent page that
// positions the strip bottom-right itself, so the scene item is a plain
// 1920x1080 source at 0,0. (The Top 8 PNG export keeps its own baked,
// capture-only strip since a browser source can't render into that server-side
// screenshot.)
// Placement is UNIFORM across every scene: the item is always at 0,0 and the
// strip's position comes solely from the shared page. Per-scene item offsets
// were tried and removed — the legal line has to read identically wherever it
// appears, and one page + one transform means moving it is a single CSS edit
// there rather than a per-scene nudge here.
const COPYRIGHT_NAME = 'Bandai Copyright';
const COPYRIGHT_PATH = '/overlay/bandai_copyright.html';
const copyrightSource = (url) => ({
  prev_ver: PREV_VER,
  name: COPYRIGHT_NAME,
  uuid: newId(),
  id: 'browser_source',
  versioned_id: 'browser_source',
  settings: { url, width: 1920, height: 1080 },
  mixers: 255,
  sync: 0,
  flags: 0,
  volume: 1.0,
  balance: 0.5,
  enabled: true,
  muted: false,
  'push-to-mute': false,
  'push-to-mute-delay': 0,
  'push-to-talk': false,
  'push-to-talk-delay': 0,
  hotkeys: {
    'libobs.mute': [],
    'libobs.unmute': [],
    'libobs.push-to-mute': [],
    'libobs.push-to-talk': [],
    'ObsBrowser.Refresh': [],
  },
  deinterlace_mode: 0,
  deinterlace_field_order: 0,
  monitoring_type: 0,
  private_settings: {},
});

// An overlay's browser source name, which the console's OBS control uses to
// find In-Match's two sources (obsStage.js).
export const layoutSourceName = (overlayName) => `${overlayName} Layout`;

const browserOverlaySource = (overlayName, url, { filters = [], restartWhenActive = false, shutdownWhenHidden = false } = {}) => ({
  prev_ver: PREV_VER,
  name: layoutSourceName(overlayName),
  uuid: newId(),
  id: 'browser_source',
  versioned_id: 'browser_source',
  // restart_when_active = OBS's "Refresh browser when scene becomes active";
  // shutdown = "Shutdown source when not visible".
  settings: {
    url,
    width: 1920,
    height: 1080,
    ...(restartWhenActive ? { restart_when_active: true } : {}),
    ...(shutdownWhenHidden ? { shutdown: true } : {}),
  },
  mixers: 255,
  sync: 0,
  flags: 0,
  volume: 1.0,
  balance: 0.5,
  enabled: true,
  muted: false,
  'push-to-mute': false,
  'push-to-mute-delay': 0,
  'push-to-talk': false,
  'push-to-talk-delay': 0,
  hotkeys: {
    'libobs.mute': [],
    'libobs.unmute': [],
    'libobs.push-to-mute': [],
    'libobs.push-to-talk': [],
    'ObsBrowser.Refresh': [],
  },
  deinterlace_mode: 0,
  deinterlace_field_order: 0,
  monitoring_type: 0,
  private_settings: {},
  // OBS reads per-source filters here; omit the key entirely when there are
  // none so non-camera-feed sources match OBS's own "no filters" output.
  ...(filters.length ? { filters } : {}),
});

// OBS 30 stores both absolute and relative (canvas-normalized) coordinates.
// Relative = normalized by half the canvas height, origin at centre, so the
// scene survives a canvas-resolution change. Matches the -1.777 / -1.0 the
// exporter used to hard-code for a top-left (0,0) item.
const CANVAS_H = 1080;
const CANVAS_ASPECT = 1920 / 1080;
const relPos = (x, y) => ({ x: (2 * x) / CANVAS_H - CANVAS_ASPECT, y: (2 * y) / CANVAS_H - 1 });
const relSize = (w, h) => ({ x: (2 * w) / CANVAS_H, y: (2 * h) / CANVAS_H });

// `bounds` (a {w,h} box) makes the source scale to fit that box regardless of
// its native resolution — how each capture drops into a cam slot. Omit it and
// the item is a plain scaled source anchored at `pos`. `fill` covers the box
// instead, cropping what spills past it.
const sceneItem = ({ name, sourceUuid, id, scale = 1, pos = { x: 0, y: 0 }, bounds = null, fill = false, visible = true }) => ({
  name,
  source_uuid: sourceUuid,
  visible,
  locked: false,
  rot: 0.0,
  scale_ref: { x: 1920.0, y: 1080.0 },
  align: 5,
  // 2 = OBS_BOUNDS_SCALE_INNER — fit inside the box, keep aspect; 3 =
  // OBS_BOUNDS_SCALE_OUTER — cover it, keep aspect, with bounds_crop (OBS 30's
  // "Crop to Bounding Box") cutting off the rest.
  bounds_type: bounds ? (fill ? 3 : 2) : 0,
  bounds_align: 0,
  bounds_crop: fill,
  crop_left: 0,
  crop_top: 0,
  crop_right: 0,
  crop_bottom: 0,
  id,
  group_item_backup: false,
  pos: { x: pos.x, y: pos.y },
  pos_rel: relPos(pos.x, pos.y),
  scale: { x: scale, y: scale },
  scale_rel: { x: scale, y: scale },
  bounds: bounds ? { x: bounds.w, y: bounds.h } : { x: 0.0, y: 0.0 },
  bounds_rel: bounds ? relSize(bounds.w, bounds.h) : { x: 0.0, y: 0.0 },
  scale_filter: 'disable',
  blend_method: 'default',
  blend_type: 'normal',
  show_transition: { duration: 0 },
  hide_transition: { duration: 0 },
  private_settings: {},
});

const sceneSource = ({ name, items }) => ({
  prev_ver: PREV_VER,
  name,
  uuid: newId(),
  id: 'scene',
  versioned_id: 'scene',
  settings: {
    // OBS numbers the next item it adds from here, so it starts past the
    // highest id in the scene (the item ids are fixed per role, up to 23).
    id_counter: Math.max(items.length + 4, ...items.map((i) => i.id)),
    custom_size: false,
    items,
  },
  mixers: 0,
  sync: 0,
  flags: 0,
  volume: 1.0,
  balance: 0.5,
  enabled: true,
  muted: false,
  'push-to-mute': false,
  'push-to-mute-delay': 0,
  'push-to-talk': false,
  'push-to-talk-delay': 0,
  hotkeys: {
    'OBSBasic.SelectScene': [],
  },
  deinterlace_mode: 0,
  deinterlace_field_order: 0,
  monitoring_type: 0,
  canvas_uuid: SCENE_CANVAS_UUID,
  private_settings: {},
});

export function buildObsSceneCollection({ packageName, packageSlug, overlays }) {
  if (!overlays?.length) {
    throw new Error('No overlays to export.');
  }
  // The TWT logo and the Bandai legal line belong to Tekken 8's events; no
  // other package's export carries them.
  const isTekken = packageSlug === 'tekken-8';
  const stinger = STINGERS[packageSlug] ?? null;
  const sound = soundOf(packageSlug);
  const mic = micOf(packageSlug);

  const gameWindow = gameWindowOf(packageSlug);
  const gameCapture = gameCaptureSource({ captureAudio: sound.captureAudio, target: gameWindow });
  const music = sound.music ? musicSource(sound.music.name, sound.music.path) : null;

  const browserSources = overlays.map((o) => ({
    overlay: o,
    source: browserOverlaySource(o.name, o.url, {
      filters: CHROMA_KEY_SLUGS.has(o.slug) ? [chromaKeyFilter()] : [],
      restartWhenActive: RESTART_WHEN_ACTIVE_SLUGS.has(o.slug),
      shutdownWhenHidden: SHUTDOWN_WHEN_HIDDEN_SLUGS.has(o.slug),
    }),
  }));

  // Hidden twin of the in-match overlay inside the SAME scene: the TO flips
  // player sides by toggling which of the two sources is visible (both
  // hotkey-able in OBS) instead of leaving OBS for the console checkbox.
  const inMatch = browserSources.find(({ overlay }) => overlay.slug === IN_MATCH_SLUG);
  const inMatchSwapped = inMatch
    ? browserOverlaySource(
        `${inMatch.overlay.name}${IN_MATCH_SWAPPED_SUFFIX}`,
        swappedOverlayUrl(inMatch.overlay.url),
      )
    : null;

  // Hidden "TWT LOGO" in the In-Match scene only (matches the reference client
  // setup), pointed at our HOSTED copy via the grant's own overlay origin.
  const twtLogo = isTekken && inMatch ? twtLogoSource(`${originOf(inMatch.overlay.url)}${TWT_LOGO_PATH}`) : null;

  // Mandated legal line, added to every scene but hidden by default (replaces
  // the per-overlay baked strip). Origin from the first grant's overlay URL
  // (no hard-coded domain).
  const bandaiCopyright = isTekken
    ? copyrightSource(`${originOf(browserSources[0].overlay.url)}${COPYRIGHT_PATH}`)
    : null;

  const cameraCommentator = CAMERA_COMMENTATOR_PACKAGES.has(packageSlug);
  const boothCommentator = BOOTH_COMMENTATOR_PACKAGES.has(packageSlug);
  const isCameraCommentator = (overlay) => cameraCommentator && overlay.slug === COMMENTATOR_SLUG;
  const usesGameCapture = (overlay) => !NO_GAME_CAPTURE_SLUGS.has(overlay.slug) && !isCameraCommentator(overlay);
  const isCameraFeed = (overlay) => overlay.slug === CAMERA_FEED_SLUG;
  const anyGameCapture = browserSources.some(({ overlay }) => usesGameCapture(overlay));
  const anyCameraFeed = browserSources.some(({ overlay }) => isCameraFeed(overlay));
  const anyCommentator = browserSources.some(({ overlay }) => overlay.slug === COMMENTATOR_SLUG);

  // Camera Feed: REDRAGON face-cam (left box) + the Logitech 'Crowd' (hidden
  // alternate). Commentator: its own Logitech "Commentator Cam" (device to be
  // retargeted later), or on the KEC packages a camera per window (see
  // COMMENTATOR_WINDOWS), with no device picked. Created only when the
  // matching overlay is granted.
  const faceCam = anyCameraFeed ? dshowSource('Video Capture Device', CAM_DEVICE) : null;
  const crowdCam = anyCameraFeed ? dshowSource('Crowd', CROWD_DEVICE) : null;
  const commentatorCam = anyCommentator && !cameraCommentator ? dshowSource('Commentator Cam', CROWD_DEVICE) : null;
  const windowCams = anyCommentator && cameraCommentator && !boothCommentator
    ? [
        ['left', COMMENTATOR_CAMERAS.left, true],
        ['right', COMMENTATOR_CAMERAS.right, true],
        ['one', COMMENTATOR_CAMERAS.one, false],
      ].map(([key, name, visible]) => ({ box: COMMENTATOR_WINDOWS[key], visible, source: dshowSource(name, null, { deactivateWhenHidden: true, muted: true }) }))
    : [];
  // The booth commentator scene (VALORANT): the Up Next and Bracket
  // overlays' own browser sources under the pair's windows and one camera,
  // hidden, under the one window of "Camera only", at the design's places;
  // on connect the OBS Control puts them where the page's layout settings
  // have the windows, and shows, hides and slides them with the page's
  // layout. An overlay not granted leaves its window empty.
  const windowSource = (slug) => browserSources.find(({ overlay }) => overlay.slug === slug)?.source ?? null;
  const booth = anyCommentator && cameraCommentator && boothCommentator
    ? {
        windows: commentatorWindows(),
        camera: dshowSource(COMMENTATOR_SOURCES.camera, null, { deactivateWhenHidden: true, muted: true }),
        left: windowSource(COMMENTATOR_WINDOW_OVERLAYS.left),
        right: windowSource(COMMENTATOR_WINDOW_OVERLAYS.right),
      }
    : null;

  const scenes = browserSources.map(({ overlay, source }) => {
    const browserItem = sceneItem({ name: source.name, sourceUuid: source.uuid, id: 16 });

    // Camera-feed scene: a capture source fit to each cam box — the REDRAGON
    // face-cam left, Game Capture right — behind the keyed browser (last item =
    // front, so the overlay stays on top and the green reveals the captures).
    // The Logitech 'Crowd' ships hidden over the player box as an alternate.
    // The KEC commentator scenes: VALORANT's booth has the Up Next and Bracket
    // overlays under the pair's windows and the camera, hidden, under the one
    // window; Marvel Rivals' has the cameras under the page, the pair's shown
    // and the one-window camera hidden. No Game Capture on either.
    // Every other scene keeps the full-canvas Game Capture, fitted to the
    // 1920x1080 canvas whatever size the capture is (a fixed 0.75 scale fitted
    // only a 2560x1440 capture; a 1080p one filled three-quarters of it).
    const items = isCameraCommentator(overlay) && booth
      ? [
          ...(booth.left ? [sceneItem({
            name: booth.left.name, sourceUuid: booth.left.uuid, id: 21,
            pos: { x: booth.windows.camera.pair.x, y: booth.windows.camera.pair.y },
            bounds: { w: booth.windows.camera.pair.w, h: booth.windows.camera.pair.h }, fill: true,
          })] : []),
          ...(booth.right ? [sceneItem({
            name: booth.right.name, sourceUuid: booth.right.uuid, id: 22,
            pos: { x: booth.windows.game.pair.x, y: booth.windows.game.pair.y },
            bounds: { w: booth.windows.game.pair.w, h: booth.windows.game.pair.h }, fill: true,
          })] : []),
          sceneItem({
            name: booth.camera.name, sourceUuid: booth.camera.uuid, id: 23, visible: false,
            pos: { x: booth.windows.camera.one.x, y: booth.windows.camera.one.y },
            bounds: { w: booth.windows.camera.one.w, h: booth.windows.camera.one.h }, fill: true,
          }),
          browserItem,
        ]
      : isCameraCommentator(overlay)
      ? [
          ...windowCams.map(({ box, visible, source: cam }, i) => sceneItem({
            name: cam.name, sourceUuid: cam.uuid, id: 21 + i, visible,
            pos: { x: box.x, y: box.y }, bounds: { w: box.w, h: box.h }, fill: true,
          })),
          browserItem,
        ]
      : isCameraFeed(overlay)
      ? [
          sceneItem({
            name: faceCam.name, sourceUuid: faceCam.uuid, id: 12,
            pos: { x: CAM_BOXES.player.x, y: CAM_BOXES.player.y },
            bounds: { w: CAM_BOXES.player.w, h: CAM_BOXES.player.h },
          }),
          sceneItem({
            name: gameCapture.name, sourceUuid: gameCapture.uuid, id: 10,
            pos: { x: CAM_BOXES.game.x, y: CAM_BOXES.game.y },
            bounds: { w: CAM_BOXES.game.w, h: CAM_BOXES.game.h },
          }),
          sceneItem({
            name: crowdCam.name, sourceUuid: crowdCam.uuid, id: 13, visible: false,
            pos: { x: CAM_BOXES.player.x, y: CAM_BOXES.player.y },
            bounds: { w: CAM_BOXES.player.w, h: CAM_BOXES.player.h },
          }),
          browserItem,
        ]
      : [
          ...(usesGameCapture(overlay)
            ? [sceneItem({ name: gameCapture.name, sourceUuid: gameCapture.uuid, id: 10, bounds: { w: 1920, h: 1080 } })]
            : []),
          browserItem,
          // swapped twin ships hidden; visible-toggle is the side switch
          ...(overlay.slug === IN_MATCH_SLUG && inMatchSwapped
            ? [sceneItem({ name: inMatchSwapped.name, sourceUuid: inMatchSwapped.uuid, id: 17, visible: false })]
            : []),
          // hosted TWT logo, also hidden — bottom-centre, matching the client's
          // placement (600×409 source scaled to ~276×188 on the canvas).
          ...(overlay.slug === IN_MATCH_SLUG && twtLogo
            ? [sceneItem({ name: twtLogo.name, sourceUuid: twtLogo.uuid, id: 18, visible: false, pos: { x: 822, y: 876 }, scale: 0.46 })]
            : []),
          // commentator face-cam (Logitech for now); hidden — the operator
          // sizes/positions it over the commentator overlay after import.
          ...(overlay.slug === COMMENTATOR_SLUG && commentatorCam
            ? [sceneItem({ name: commentatorCam.name, sourceUuid: commentatorCam.uuid, id: 19, visible: false })]
            : []),
        ];

    // The music under everything, on every scene but the match's.
    const musicItem = music && overlay.slug !== IN_MATCH_SLUG
      ? [sceneItem({ name: music.name, sourceUuid: music.uuid, id: 9 })]
      : [];

    // Legal line composited on top of every scene (last item = front in OBS),
    // shipped hidden for the operator to enable. Same source, same 0,0
    // transform on every scene — see COPYRIGHT_NAME above.
    return sceneSource({
      name: overlay.name,
      items: bandaiCopyright
        ? [...musicItem, ...items, sceneItem({
          name: bandaiCopyright.name,
          sourceUuid: bandaiCopyright.uuid,
          id: 20,
          visible: false,
        })]
        : [...musicItem, ...items],
    });
  });

  const firstSceneName = scenes[0].name;

  return {
    name: packageName,
    ...(sound.desktopAudio ? { DesktopAudioDevice1: audioBusDevice('Desktop Audio', 'wasapi_output_capture') } : {}),
    AuxAudioDevice1: audioBusDevice(mic?.name ?? 'Mic/Aux', 'wasapi_input_capture', { mono: mic?.mono === true }),
    groups: [],
    scene_order: scenes.map((s) => ({ name: s.name })),
    current_scene: firstSceneName,
    current_program_scene: firstSceneName,
    canvases: [],
    current_transition: stinger ? stinger.name : 'Fade',
    transition_duration: 300,
    transitions: stinger ? [stingerTransition(stinger, { crossfade: !!music })] : [],
    quick_transitions: [
      { name: 'Cut',  duration: 300, hotkeys: [], id: 1, fade_to_black: false },
      { name: 'Fade', duration: 300, hotkeys: [], id: 2, fade_to_black: false },
      { name: 'Fade', duration: 300, hotkeys: [], id: 3, fade_to_black: true },
    ],
    saved_projectors: [],
    preview_locked: false,
    scaling_enabled: false,
    scaling_level: -2,
    scaling_off_x: 0.0,
    scaling_off_y: 0.0,
    'virtual-camera': { type2: 3 },
    modules: {
      'scripts-tool': [],
      'output-timer': {
        streamTimerHours: 0,
        streamTimerMinutes: 0,
        streamTimerSeconds: 30,
        recordTimerHours: 0,
        recordTimerMinutes: 0,
        recordTimerSeconds: 30,
        autoStartStreamTimer: false,
        autoStartRecordTimer: false,
        pauseRecordTimer: true,
      },
      'auto-scene-switcher': {
        interval: 300,
        non_matching_scene: '',
        switch_if_not_matching: false,
        active: false,
        switches: [],
      },
      captions: { source: '', enabled: false, lang_id: 1033, provider: 'mssapi' },
    },
    version: 2,
    sources: [
      ...(anyGameCapture ? [gameCapture] : []),
      ...(faceCam ? [faceCam] : []),
      ...(crowdCam ? [crowdCam] : []),
      ...(commentatorCam ? [commentatorCam] : []),
      ...windowCams.map((c) => c.source),
      ...(booth ? [booth.camera] : []),
      ...(music ? [music] : []),
      ...browserSources.map((b) => b.source),
      ...(inMatchSwapped ? [inMatchSwapped] : []),
      ...(twtLogo ? [twtLogo] : []),
      ...(bandaiCopyright ? [bandaiCopyright] : []),
      ...scenes,
    ],
  };
}

export function downloadObsSceneCollection({ packageName, packageSlug, overlays }) {
  const json = buildObsSceneCollection({ packageName, packageSlug, overlays });
  const blob = new Blob([JSON.stringify(json, null, 4)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${packageSlug}-obs-scenes.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
